import type { FormEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Loader2,
  MapPin,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSubmitGalaRsvp } from '@/lib/api/queries/useGalaRsvp'

const EVENT_DATE_LABEL = 'Sunday, March 29, 2026'
const INVITE_IMAGE_PATH = '/gala-dinner-invite.jpg'

const eventMoments = [
  {
    icon: CalendarDays,
    label: 'Date',
    value: EVENT_DATE_LABEL,
  },
  {
    icon: Clock3,
    label: 'Time',
    value: '4:00 PM to 8:00 PM',
  },
  {
    icon: MapPin,
    label: 'Venue',
    value: 'Glass House, Malad',
  },
]

export function GalaRsvpPage() {
  const [name, setName] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [inviteImageMissing, setInviteImageMissing] = useState(false)
  const { mutate, isPending, isSuccess, error, reset } = useSubmitGalaRsvp()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const normalizedName = useMemo(() => name.trim().replace(/\s+/g, ' '), [name])
  const submitError = formError ?? (error instanceof Error ? error.message : null)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError(null)

    if (normalizedName.length < 2) {
      setFormError('Please enter your full name before submitting.')
      return
    }

    mutate(
      { name: normalizedName },
      {
        onSuccess: () => {
          setName('')
        },
      }
    )
  }

  const handleReset = () => {
    reset()
    setFormError(null)
    setName('')
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#080604] text-white">
      <div className="relative isolate">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,205,102,0.18),_transparent_30%),radial-gradient(circle_at_bottom,_rgba(171,62,17,0.2),_transparent_28%),linear-gradient(180deg,_#120a05_0%,_#080604_45%,_#050304_100%)]" />
        <div className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top,_rgba(255,232,177,0.25),_transparent_60%)]" />
        <div className="absolute left-1/2 top-16 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-300/10 blur-3xl" />
        <div className="absolute left-10 top-32 h-32 w-32 rounded-full border border-amber-200/10" />
        <div className="absolute right-10 top-24 h-40 w-40 rounded-full border border-amber-200/10" />

        <main className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
          <div className="mx-auto mb-8 max-w-3xl text-center lg:mb-10">
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-amber-200/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.38em] text-amber-100/80 backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              YPL Season 11
            </div>
            <h1 className="font-editorial text-5xl font-semibold leading-none tracking-[0.06em] text-amber-100 sm:text-6xl">
              Gala Dinner Night
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm text-amber-50/70 sm:text-base">
              An evening of celebration, stories, and the start of another unforgettable YPL season.
            </p>
          </div>

          <div className="grid flex-1 items-start gap-6 lg:grid-cols-[minmax(0,1.1fr)_420px] xl:gap-10">
            <section className="relative">
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-amber-300/10 via-transparent to-transparent blur-2xl" />
              <div className="relative rounded-[2rem] border border-amber-200/15 bg-white/[0.03] p-3 shadow-[0_20px_80px_rgba(0,0,0,0.45)] backdrop-blur-sm">
                <div className="overflow-hidden rounded-[1.55rem] border border-amber-100/10 bg-[#130c08]">
                  {!inviteImageMissing ? (
                    <img
                      src={INVITE_IMAGE_PATH}
                      alt="YPL Season 11 Gala Dinner invitation"
                      className="h-full w-full object-cover"
                      onError={() => setInviteImageMissing(true)}
                    />
                  ) : (
                    <div className="relative flex min-h-[720px] flex-col justify-between overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(255,209,120,0.16),_transparent_38%),linear-gradient(180deg,_#110a06_0%,_#17110a_55%,_#0a0603_100%)] p-8 sm:p-10">
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22140%22 height=%22140%22 viewBox=%220 0 140 140%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22rgba(255,214,141,0.08)%22 fill-opacity=%221%22%3E%3Ccircle cx=%221%22 cy=%221%22 r=%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
                      <div className="relative text-center">
                        <p className="font-sans-luxe text-xs uppercase tracking-[0.55em] text-amber-100/75">
                          YPL Season 11
                        </p>
                        <h2 className="font-editorial mt-8 text-5xl leading-none text-amber-100 sm:text-6xl">
                          Gala Dinner
                        </h2>
                        <p className="font-editorial mt-3 text-3xl text-amber-200/90 sm:text-4xl">
                          You Are Invited
                        </p>
                      </div>

                      <div className="relative space-y-5 rounded-[1.5rem] border border-amber-100/10 bg-black/15 px-5 py-6 backdrop-blur-sm sm:px-6">
                        {eventMoments.map(({ icon: Icon, label, value }) => (
                          <div key={label} className="flex items-start gap-4">
                            <div className="mt-1 rounded-full border border-amber-100/20 bg-amber-100/10 p-2 text-amber-200">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-sans-luxe text-xs uppercase tracking-[0.32em] text-amber-50/55">
                                {label}
                              </p>
                              <p className="font-editorial text-2xl leading-tight text-amber-50">
                                {value}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="relative text-center">
                        <p className="font-sans-luxe text-sm uppercase tracking-[0.42em] text-amber-100/60">
                          For all players
                        </p>
                        <p className="font-editorial mt-6 text-4xl text-amber-100">
                          Where the season begins.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="relative">
              <Card className="overflow-hidden border border-amber-100/15 bg-white/[0.06] shadow-[0_18px_60px_rgba(0,0,0,0.42)] backdrop-blur-xl">
                <div className="border-b border-amber-100/10 bg-gradient-to-r from-amber-400/12 via-transparent to-transparent px-6 py-6">
                  <p className="font-sans-luxe text-xs uppercase tracking-[0.34em] text-amber-100/65">
                    RSVP
                  </p>
                  <h2 className="font-editorial mt-3 text-4xl leading-none text-amber-50">
                    Save your seat
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-amber-50/70">
                    Enter your name and confirm your attendance for the gala dinner on March 29, 2026.
                  </p>
                </div>

                <div className="space-y-6 p-6">
                  <div className="grid gap-3 rounded-[1.5rem] border border-amber-100/10 bg-black/15 p-4">
                    {eventMoments.map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex items-start gap-3">
                        <div className="rounded-full border border-amber-100/10 bg-amber-100/10 p-2 text-amber-200">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-sans-luxe text-[11px] uppercase tracking-[0.28em] text-amber-50/50">
                            {label}
                          </p>
                          <p className="mt-1 text-sm font-medium text-amber-50">
                            {value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {isSuccess ? (
                    <div className="space-y-5 rounded-[1.75rem] border border-emerald-400/25 bg-emerald-400/10 p-5 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-300/15 text-emerald-200">
                        <CheckCircle2 className="h-7 w-7" />
                      </div>
                      <div>
                        <p className="font-sans-luxe text-xs uppercase tracking-[0.34em] text-emerald-100/75">
                          RSVP Received
                        </p>
                        <h3 className="font-editorial mt-2 text-3xl text-emerald-50">
                          We’ll see you there
                        </h3>
                        <p className="mt-3 text-sm leading-6 text-emerald-50/75">
                          Your name has been added to the gala dinner guest list.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleReset}
                        className="w-full border-emerald-200/25 bg-white/10 text-white hover:bg-white/15 hover:text-white"
                      >
                        Submit another RSVP
                      </Button>
                    </div>
                  ) : (
                    <form className="space-y-5" onSubmit={handleSubmit}>
                      <div className="space-y-2">
                        <Label htmlFor="guest-name" className="font-sans-luxe text-sm text-amber-50/90">
                          Your name
                        </Label>
                        <Input
                          id="guest-name"
                          type="text"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          placeholder="Enter your full name"
                          autoComplete="name"
                          className="border-amber-100/15 bg-white/10 text-white placeholder:text-amber-50/35 hover:border-amber-100/25 focus:border-amber-300/40 focus:ring-amber-200/10"
                        />
                      </div>

                      {submitError ? (
                        <div className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                          {submitError}
                        </div>
                      ) : null}

                      <div className="rounded-[1.5rem] border border-amber-100/10 bg-white/[0.03] p-4">
                        <p className="font-sans-luxe text-xs uppercase tracking-[0.32em] text-amber-100/55">
                          Quick note
                        </p>
                        <p className="mt-2 text-sm leading-6 text-amber-50/70">
                          This RSVP is only used for the gala dinner attendance list. One response takes just a few seconds.
                        </p>
                      </div>

                      <Button
                        type="submit"
                        size="xl"
                        variant="premium"
                        disabled={isPending}
                        className="w-full rounded-2xl bg-[linear-gradient(135deg,#f5d17c_0%,#d3a64b_45%,#f8e8b6_100%)] text-[#2f1908] shadow-[0_18px_38px_rgba(212,166,75,0.28)] hover:brightness-105"
                      >
                        {isPending ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Saving your RSVP...
                          </>
                        ) : (
                          <>
                            RSVP Now
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </div>
  )
}
